import MuiChip, { ChipProps as MuiChipProps } from '@mui/material/Chip';
import React, { forwardRef } from 'react';
import { BpDesignSystem } from '../../types';
import { Icon, type IconName, type IconProps } from '../Icon';
import { IconButton } from '../IconButton';
import { OverFlowMenu, OverFlowMenuProps } from '../OverFlowMenu';
import SvgIcon from '@mui/material/SvgIcon';
import { styled } from '../../styles';
import { isObject } from '../../utils/guards';

export type ChipProps = BpDesignSystem.PropsWithChildren<
  Omit<MuiChipProps, 'icon' | 'deleteIcon'> & {
    dataTestLabel?: string;
    iconName?: IconName;
    deleteIconName?: IconName;
    slotProps?: {
      icon?: Omit<IconProps, 'name'>;
      deleteIcon?: Omit<IconProps, 'name' | 'ref'>;
      overflowMenu?: Omit<OverFlowMenuProps, 'actions'>;
    };
    actions?: OverFlowMenuProps['actions'];
  }
>;

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      dataTest = 'bpds-chip',
      color = 'default',
      slotProps,
      actions,
      iconName,
      onDelete,
      deleteIconName,
      dataTestLabel,
      ...props
    },
    ref
  ) => {
    return (
      <StyledMuiChip
        ref={ref}
        color={color}
        icon={
          <span className={`icon-container ${iconName ? 'show-icon' : ''}`}>
            {iconName ? (
              <Icon
                size={props.size}
                color="inherit"
                data-type="chip-icon"
                name={iconName}
                {...slotProps?.icon}
                style={{ ...slotProps?.icon?.style }}
              />
            ) : undefined}
          </span>
        }
        onDelete={actions?.length ? (actions?.length === 1 && actions[0] ? actions[0].run : noop) : onDelete}
        deleteIcon={
          (actions?.length ?? 0) > 1 ? (
            <OverFlowMenu
              iconSize="small"
              iconStyle={{ padding: 5, marginRight: 3 }}
              dataTest="bpds-chip-overflow-menu"
              {...slotProps?.overflowMenu}
              actions={actions}
            />
          ) : actions?.length === 1 && actions[0] ? (
            <IconButton
              dataTest="bpds-chip-action-button"
              iconName={actions[0].icon ?? deleteIconName ?? 'add'}
              style={{ padding: 5, marginRight: 3 }}
              slotProps={{
                tooltip: { title: actions[0].label }
              }}
              size="small"
            />
          ) : deleteIconName ? (
            <Icon
              size={props.size}
              color="inherit"
              dataTest="bpds-chip-delete-button"
              name={deleteIconName}
              {...slotProps?.deleteIcon}
            />
          ) : (
            <DeleteIcon dataTest="bpds-chip-delete-button" {...slotProps?.deleteIcon} />
          )
        }
        {...props}
        data-test={dataTest}
        data-test-label={dataTestLabel ?? getDataTestLabel(props.label)}
      />
    );
  }
);

const StyledMuiChip = styled(MuiChip)`
    transition: color, background-color 0.2s ease-in-out;
    .MuiSvgIcon-root.MuiChip-deleteIcon {
        transition: color 0.2s ease-in-out;
        &:focus {
            color: ${({ theme }) => theme.tokens.colorOnAlert};
        }
    }
    &:has(.MuiSvgIcon-root.MuiChip-deleteIcon:focus) {
        background-color: ${({ theme }) => theme.tokens.colorAlert};
        color: ${({ theme }) => theme.tokens.colorOnAlert};
    }
`;

function noop() {}

function getDataTestLabel(label?: React.ReactNode): string {
  if (!label) {
    return '';
  }
  if (typeof label === 'string') {
    return label;
  }
  if (typeof label === 'number') {
    return label.toString();
  }
  if (isObject(label) && 'props' in label) {
    if (typeof label.props.children === 'string') {
      return label.props.children;
    }
    if ('selectedOption' in label.props && label.props.selectedOption?.label) {
      return label.props.selectedOption.label;
    }
  }
  return '';
}

function DeleteIcon({ dataTest, size, tabIndex = 0, color, ...rest }: Omit<IconProps, 'name' | 'ref'>) {
  return (
    // @ts-ignore - svg icon is not compatible with icons props, but we want to reuse the same props for styling and data-test attributes
    <SvgIcon
      data-test={dataTest}
      fontSize={size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium'}
      tabIndex={tabIndex}
      style={{
        outline: 'none'
      }}
      {...rest}
    >
      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
    </SvgIcon>
  );
}
